import { useMemo, useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { Modal } from '../componentes/Modal'
import { IconoActualizar, IconoFactura, IconoLogistica } from '../componentes/iconos'
import { usePartesReparto, type ResumenParte } from '../hooks/usePartesReparto'
import { RutaDelDia } from './calendario/RutaDelDia'

/**
 * Gestión › Partes de reparto (trazabilidad). Histórico de las rutas que se
 * ENVIARON al repartidor, un registro por día: nº de paradas, grupos, notas de
 * entrega y la hora real de envío del correo. "Ver ruta" abre el detalle del día
 * (mismo componente que el calendario). Estilo: resumen de semana arriba (B) +
 * tabla con columnas Notas y Envío (C).
 */

function fmtDia(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' })
}

function horaCorta(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

/** Métricas de los últimos 5 días con parte (una "semana" operativa). */
function Resumen({ partes }: { partes: ResumenParte[] }) {
  const sem = partes.slice(0, 5)
  const repartos = sem.reduce((a, p) => a + p.totalParadas, 0)
  const notas = sem.reduce((a, p) => a + p.totalNotas, 0)
  const horas = sem.map((p) => new Date(p.enviadoEn)).filter((d) => !Number.isNaN(d.getTime()))
  const mediaMin = horas.length
    ? Math.round(horas.reduce((a, d) => a + d.getHours() * 60 + d.getMinutes(), 0) / horas.length)
    : null
  const media = mediaMin != null ? `${String(Math.floor(mediaMin / 60)).padStart(2, '0')}:${String(mediaMin % 60).padStart(2, '0')}` : '—'
  return (
    <div className="mb-4 flex flex-wrap gap-6 rounded-xl bg-elevado px-4 py-3">
      <div className="text-xs text-tinta-apagada">
        <b className="block text-lg tabular-nums text-tinta">{repartos}</b>
        repartos (últ. 5 días)
      </div>
      <div className="text-xs text-tinta-apagada">
        <b className="block text-lg tabular-nums text-tinta">{sem.length}/5</b>
        días con ruta
      </div>
      <div className="text-xs text-tinta-apagada">
        <b className="block text-lg tabular-nums text-tinta">{notas}</b>
        notas de entrega
      </div>
      <div className="text-xs text-tinta-apagada">
        <b className="block text-lg tabular-nums text-tinta">{media}</b>
        media de envío
      </div>
    </div>
  )
}

export function PartesReparto() {
  const { data: partes, isLoading, isError, error, refetch, isFetching } = usePartesReparto()
  const [diaAbierto, setDiaAbierto] = useState<string | null>(null)
  const lista = useMemo(() => partes ?? [], [partes])

  if (isLoading) return <SkeletonList rows={6} />
  if (isError) return <EstadoError error={error} onReintentar={() => refetch()} />

  if (lista.length === 0) {
    return (
      <EstadoVacio
        titulo="Aún no hay partes de reparto"
        descripcion="Cuando se envíe la ruta diaria al repartidor (cada día laborable a las 5:00), quedará registrada aquí."
      />
    )
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-tinta">
          <IconoLogistica className="size-4 text-acento" />
          Rutas enviadas al repartidor
        </h3>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-tinta-2 ring-1 ring-borde hover:bg-lienzo"
        >
          <IconoActualizar className={`size-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      <Resumen partes={lista} />

      <div className="overflow-hidden rounded-xl border border-borde">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-elevado">
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Día</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Paradas</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Grupos</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Notas</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Envío</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {lista.map((p) => (
              <tr key={p.fecha} className="border-t border-borde">
                <td className="px-3 py-2.5 font-medium capitalize text-tinta">{fmtDia(p.fecha)}</td>
                <td className="px-3 py-2.5 tabular-nums text-tinta">{p.totalParadas}</td>
                <td className="px-3 py-2.5 tabular-nums text-tinta">{p.totalGrupos}</td>
                <td className="px-3 py-2.5">
                  {p.totalNotas > 0 ? (
                    <span className="inline-flex items-center gap-1 font-semibold text-aviso-tinta">
                      <IconoFactura className="size-3.5 text-aviso-icono" />
                      {p.totalNotas}
                    </span>
                  ) : (
                    <span className="tabular-nums text-tinta-apagada">0</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-tinta">
                    <span className="size-2 rounded-full bg-positivo-punto" aria-hidden />
                    {horaCorta(p.enviadoEn)}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    onClick={() => setDiaAbierto(p.fecha)}
                    className="rounded-md border border-acento-borde bg-acento-suave px-2.5 py-1 text-xs font-semibold text-acento-tinta hover:brightness-95"
                  >
                    Ver ruta
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detalle del día: reutiliza el mismo bloque de ruta del calendario. */}
      {diaAbierto ? (
        <Modal titulo={`Ruta del ${fmtDia(diaAbierto)}`} onCerrar={() => setDiaAbierto(null)} anchoMax="max-w-lg">
          <div className="px-5 py-4">
            <RutaDelDia iso={diaAbierto} />
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
